import React from "react";
import { Link } from "react-router-dom";
import { Arrow, Mark } from "./Icons";
import SiteMeta from "./SiteMeta";

export default function About() {
  return (
    <>
      <SiteMeta
        title="About"
        path="/about"
        description="About Hunter Grey: a developer building personal tools and self-hosted systems, with a background in AI, robotics, and electrical engineering."
      />
      <header className="page-intro">
        <h1>
          About Grey<span>.</span>
        </h1>
        <p>
          I like understanding how things work. Building my own tools is one way
          I keep learning.
        </p>
      </header>
      <div className="about-intro">
        <div className="about-copy">
          <p>
            These days, I work on developer tools, apps that keep working
            offline, and the infrastructure behind my own services. That usually
            means moving between Rust, Go, frontend code, and Linux.
          </p>
          <p>
            My background is in electrical engineering, AI, and robotics. I
            started with signals and systems; now I spend more time thinking
            about editors, synchronization, and how software fits into everyday
            life.
          </p>
          <Link className="text-link" to="/projects">
            See what I’m building <Arrow />
          </Link>
        </div>
        <aside className="about-card">
          <Mark />
          <h2>A workbench of my own.</h2>
          <p>
            Write the software.
            <br />
            Run the services.
            <br />
            Keep learning how it all fits.
          </p>
        </aside>
      </div>
      <section className="about-section" aria-labelledby="interests-title">
        <div>
          <h2 id="interests-title">Away from the editor</h2>
        </div>
        <div>
          <p>
            My homelab is another ongoing project: containers, virtual machines,
            OpenWrt, and configuration kept as code. I also experiment with
            running Linux on Android and build small tools for daily routines.
          </p>
          <p>
            It’s the same curiosity at a different scale: make something useful,
            understand the moving parts, and keep improving it.
          </p>
        </div>
      </section>
      <section
        className="about-section"
        id="experience"
        tabIndex={-1}
        aria-labelledby="experience-title"
      >
        <div>
          <h2 id="experience-title">A little background</h2>
          <p>Research and early experience.</p>
        </div>
        <div className="experience-list">
          <article>
            <div className="entry-heading">
              <h3>AI &amp; Robotics Lab</h3>
              <span>2021–2023</span>
            </div>
            <p className="entry-role">Research assistant, Hainan University</p>
            <p>
              Worked on deep learning for EEG signal decoding, alongside
              research in robotics and ROS.
            </p>
          </article>
          <article>
            <div className="entry-heading">
              <h3>Mojia Interactive</h3>
              <span>2022</span>
            </div>
            <p className="entry-role">Network engineering internship</p>
            <p>
              Built proxy infrastructure and configured network routing on
              OpenWrt.
            </p>
          </article>
          <article>
            <div className="entry-heading">
              <h3>Benewake</h3>
              <span>2022</span>
            </div>
            <p className="entry-role">Backend development internship</p>
            <p>Helped build an ERP backend using Go, Gin, and MySQL.</p>
          </article>
        </div>
      </section>
      <section
        className="about-section"
        id="education"
        tabIndex={-1}
        aria-labelledby="education-title"
      >
        <div>
          <h2 id="education-title">Education</h2>
        </div>
        <div>
          <div className="entry-heading">
            <h3>Hainan University</h3>
            <span>2019–2023</span>
          </div>
          <p className="entry-role">
            B.Eng. in Electrical Engineering and Automation
          </p>
          <p>
            Studied machine learning, embedded systems, and robotics while
            exploring computer science and software development.
          </p>
          <Link className="text-link" to="/resume">
            Résumé <Arrow />
          </Link>
        </div>
      </section>
      <div className="about-end">
        <p>Curious about something I’m working on?</p>
        <Link className="button" to="/contact">
          Let’s talk <Arrow />
        </Link>
      </div>
    </>
  );
}
